import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeInformedConsentForTesting } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface InformedConsentForTestingFormData {
    student: string;
    formId: string;
    isInitialIntake: boolean;
    isPsychologicalTesting: boolean;
    isEvaluationsTesting: boolean;
    isFeedback: boolean;
    fullName: string;
    ssnFour: string | null;
    todayDate: string | null;
}

interface InformedConsentForTestingFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<InformedConsentForTestingFormData, InformedConsentForTestingFormProps>;

const validators: Validators<InformedConsentForTestingFormData> = {
    isInitialIntake: [required({ message: "It's required." })],
    isPsychologicalTesting: [required({ message: "It's required." })],
    isEvaluationsTesting: [required({ message: "It's required." })],
    isFeedback: [required({ message: "It's required." })],
    fullName: [required({ message: 'Full Name is required.' }), maxLength({ max: 255 })],
    ssnFour: [required({ message: 'Last 4 Digits of the SSN is required.' }), minLength({ min: 4 })],
    todayDate: [required({ message: 'Date is required.' }), maxLength({ max: 255 })],
};

const InformedConsentForTestingForm: React.FunctionComponent<InjectedProps & InformedConsentForTestingFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & InformedConsentForTestingFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Informed Consent: Testing</h2>
            <div>
                This document contains important information about our professional services and business policies. This
                consent form represents an agreement between you and Manhattan Psychology Group, PC.{' '}
            </div>
            <div>Please check the box next to each paragraph to provide your consent to the above terms.</div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={13}
                wrapperConfig={{
                    label: '',
                }}
            />

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    TESTING SERVICES: Initial Intake and Diagnostic Evaluation: The first session will involve an evaluation of your family’s needs. During the initial evaluation, the clinician will ask you for detailed information about your child, including details about concerns, symptoms, and adaptive behaviors. You may be asked to complete formal measures to help us understand your child better. You may also be asked to sign consent for the clinician to contact other providers or educators who know your child well. Any relevant medical, treatment, or educational records will be requested. After evaluating your child, the clinician will discuss the results of the intake and offer you some first impressions of what the testing plan will include. The clinician will discuss with you their working understanding of the problem, testing plan, evaluation objectives, and their view of the possible outcomes of testing. If you have any unanswered questions about any of the procedures used in the course of the evaluation, their possible risks, the clinician’s expertise in employing them, or about the testing plan, please ask them and you will be answered fully. You also have the right to ask about other treatments for your child’s condition and their risks and benefits. You should evaluate this information along with your own opinion of whether you feel comfortable working with the clinician. Testing involves a commitment of time and energy, so you should be very careful about finding an evaluator with whom you feel comfortable. If you have any questions about the procedures of this clinic, you are encouraged to bring them up whenever they arise. If your doubts persist, we will work with you or refer you to another provider/service.
                </div>
                <FieldInput
                    name="isInitialIntake"
                    type="checkbox"
                    validate={validators.isInitialIntake}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    TESTING SERVICES: Psychological and neuropsychological testing is not easily described in general statements. It varies depending on the reason for referral and the problems you bring forward. There are many differ measures which may be used to assess the problems you hope to address. Specific areas of functioning, such as cognitive, academic, language, executive functioning, motor skills, adaptive functioning and social-emotional/behavioral functioning, may be assessed or other assessments may be suggested.
                </div>
                <FieldInput
                    name="isPsychologicalTesting"
                    type="checkbox"
                    validate={validators.isPsychologicalTesting}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>

            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    THE PROCESS OF TESTING AND SCOPE OF PRACTICE: Psychological and neuropsychological evaluations can have benefits and risks. Since testing often involves assessing areas of difficulty, you or your child may experience uncomfortable feelings like sadness, guilt, anger, frustration, and helplessness. On the other hand, testing has been shown to have benefits for people who go through it. Testing often leads to better interventions and recommendations to specific problems, and significant reductions in feelings of distress. Clinicians at Manhattan Psychology Group, PC are committed to providing evaluations that are deemed to be most appropriate for you and your family based on the problems discussed and assessments supported by the literature. However, there are no guarantees of what you will experience. Clinicians at Manhattan Psychology Group, PC provide neither custody evaluation recommendation nor medication or prescription recommendation nor legal advice, as these activities do not fall within her scope of practice.
                </div>
                <FieldInput
                    name="isEvaluationsTesting"
                    type="checkbox"
                    validate={validators.isEvaluationsTesting}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.checkBoxContainer)}>
                <div className={classNames(styles.checkBoxLabel)}>
                    FEEDBACK: As set forth above, during the course of feedback, your clinician will review the findings of the evaluation, recommendation and address any questions and concerns. Clinicians at Manhattan Psychology Group, PC do not work with clients who, in their opinion, they cannot help or who are not benefitting from their services. In such a case, if appropriate, your clinician will give you referrals to contact. You have the right to discontinue testing or communication at any time. If you choose to do so, upon your request and if it is appropriate and possible, Manhattan Psychology Group, PC will provide you with names of other qualified professionals who services you might prefer.
                </div>
                <FieldInput
                    name="isFeedback"
                    type="checkbox"
                    validate={validators.isFeedback}
                    wrapperConfig={{
                        label: 'Please check it to approve the above *',
                        classes: {
                            inputWrapper: 'customCheckbox customCheckboxWide ',
                        },
                    }}
                />
            </div>
            <div className={classNames(styles.textBox)}>
                By typing my full name below and last 4 digits of my SSN, I verify that I have read and understand the
                terms of treatment with MPG outlined above and in the attached welcome packet.
            </div>

            <FieldInput
                name="fullName"
                type="text"
                validate={validators.fullName}
                wrapperConfig={{
                    label: 'Full Name: *',
                }}
            />
            <FieldInput
                name="ssnFour"
                type="text"
                validate={validators.ssnFour}
                wrapperConfig={{
                    label: 'Last 4 Digits of the SSN *',
                }}
            />

            <FieldDate
                name="todayDate"
                type="text"
                date={true}
                validate={validators.todayDate}
                wrapperConfig={{
                    label: "Today's date *",
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<InformedConsentForTestingFormData, InformedConsentForTestingFormProps>({
    form: 'informed-consent-for-testing-form',
    enableReinitialize: true,
    onSubmit: async (
        data: InformedConsentForTestingFormData,
        dispatch: Dispatch<any>,
        props: InformedConsentForTestingFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeInformedConsentForTesting({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<InformedConsentForTestingFormData, InformedConsentForTestingFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(InformedConsentForTestingForm);
